import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { kpisAPI, usersAPI, periodsAPI, teamsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  Trash2,
  Settings2,
  X,
  EyeOff,
  Power,
  AlertCircle,
  Building2,
  GripVertical,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  KPICard, 
  KPIListView, 
  KPIEmptyState, 
  KPIForm, 
  KPIDetailView 
} from '../components/kpis';
import { SortableContainer, SortableItem } from '../components/SortableKPI';
import { useKPIOrder } from '../lib/useKPIOrder';

const KPIs = () => {
  const navigate = useNavigate();
  const { kpiId } = useParams();
  const { isAdmin, isViewer, canWrite, user } = useAuth();
  const { canWrite: canWritePerm } = usePermissions();
  // Write permission for KPIs: admin OR user/viewer with 'full' on kpis.kpis
  const canEditKPIs = isAdmin() || canWritePerm('kpis', 'kpis');
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('kpi-view-mode') || 'card');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKPI, setEditingKPI] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Bulk management states (Admin only)
  const [isManagementMode, setIsManagementMode] = useState(false);
  const [selectedKPIs, setSelectedKPIs] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Soft delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Edit order mode state
  const [isEditOrderMode, setIsEditOrderMode] = useState(false);
  
  // Scope kept at 'all' for backend compatibility (not exposed in UI)
  // Security/permissions by teams still enforced by backend
  const scope = 'all';

  // Always fetch data, even when viewing KPI details (for navigation)
  useEffect(() => {
    fetchData();
  }, []); // scope is now constant ('all'), no dependency needed

  useEffect(() => {
    localStorage.setItem('kpi-view-mode', viewMode);
  }, [viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // scope is fixed at 'all' - no UI control, backend handles permissions
      const [kpisRes, areasRes, periodRes] = await Promise.all([
        kpisAPI.getAll(),
        kpisAPI.getAreas(),
        periodsAPI.getCurrent(),
      ]);
      setKpis(kpisRes.data);
      setAreas(areasRes.data);
      setCurrentPeriod(periodRes.data);

      if (isAdmin()) {
        const usersRes = await usersAPI.getAll();
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      toast.error('Error al cargar los KPIs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKPI = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingKPI(null);
    setIsDialogOpen(true);
  };

  const handleSoftDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await kpisAPI.remove(deleteTarget.id);
      toast.success(`KPI "${deleteTarget.name}" eliminado. Puede restaurarlo desde la papelera.`);
      setDeleteTarget(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar KPI');
    } finally {
      setDeleting(false);
    }
  };


  const handleEditKPI = (kpi, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingKPI(kpi);
    setIsDialogOpen(true);
  };

  const handleSubmitKPI = async (formData, editingKPI) => {
    setSaving(true);
    try {
      if (editingKPI) {
        const response = await kpisAPI.update(editingKPI.id, formData);
        const updatedKPI = response.data;
        const hierarchyChanged = editingKPI.has_hierarchy && 
          JSON.stringify(editingKPI.hierarchy_nodes) !== JSON.stringify(updatedKPI.hierarchy_nodes);
        if (hierarchyChanged) {
          toast.success('KPI actualizado. La jerarquía se actualizó correctamente. Las capturas futuras usarán la nueva estructura.');
        } else {
          toast.success('KPI actualizado correctamente');
        }
      } else {
        await kpisAPI.create(formData);
        toast.success('KPI creado correctamente');
      }
      setIsDialogOpen(false);
      setEditingKPI(null);
      await fetchData(); // Await refresh to ensure fresh data before next edit
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el KPI');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = (open) => {
    if (!open) {
      setEditingKPI(null);
    }
    setIsDialogOpen(open);
  };

  const handleKPIClick = (kpi) => {
    navigate(`/kpis/${kpi.id}`);
  };

  // Memoize filteredKPIs to prevent unnecessary re-renders in child components
  const filteredKPIs = useMemo(() => kpis.filter((kpi) => {
    const matchesSearch = kpi.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kpi.area.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArea = filterArea === 'all' || kpi.area === filterArea;
    return matchesSearch && matchesArea;
  }), [kpis, searchTerm, filterArea]);

  // Use KPI ordering hook
  const {
    orderedKpis,
    loading: orderLoading,
    saving: orderSaving,
    isManualOrder,
    handleDragEnd,
    moveKpi,
    resetOrder,
    saveOrder,
  } = useKPIOrder(filteredKPIs);

  // Auto-save default order from KPIs page if no manual order exists (bootstraps single source of truth)
  useEffect(() => {
    if (!orderLoading && !isManualOrder && orderedKpis.length > 0) {
      saveOrder(orderedKpis);
    }
  }, [orderLoading, isManualOrder, orderedKpis.length]);

  // Use ordered KPIs for display (when not in management mode)
  const displayKpis = isManagementMode ? filteredKPIs : orderedKpis;

  // Bulk management functions (Admin only)
  const toggleManagementMode = () => {
    if (isManagementMode) {
      setSelectedKPIs(new Set());
    }
    setIsManagementMode(!isManagementMode);
  };

  const toggleKPISelection = (kpiId, e) => {
    e.stopPropagation();
    const newSelection = new Set(selectedKPIs);
    if (newSelection.has(kpiId)) {
      newSelection.delete(kpiId);
    } else {
      newSelection.add(kpiId);
    }
    setSelectedKPIs(newSelection);
  };

  const selectAllKPIs = () => {
    if (selectedKPIs.size === filteredKPIs.length) {
      setSelectedKPIs(new Set());
    } else {
      setSelectedKPIs(new Set(filteredKPIs.map(k => k.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedKPIs.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const kpiIds = Array.from(selectedKPIs);
      const response = await kpisAPI.bulkAction({ action, kpi_ids: kpiIds });
      
      toast.success(response.data.message || `Acción completada: ${kpiIds.length} KPIs afectados`);
      
      setIsManagementMode(false);
      setSelectedKPIs(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar la acción');
    } finally {
      setBulkActionLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const getSelectedKPINames = () => {
    return kpis.filter(k => selectedKPIs.has(k.id)).map(k => k.name);
  };

  // If we have a selected KPI, show the detail view
  if (kpiId) {
    return (
      <>
        <KPIDetailView 
          kpiId={kpiId} 
          isAdmin={canEditKPIs} 
          onEdit={(kpi) => handleEditKPI(kpi)}
          kpiList={orderedKpis}
        />
        <KPIForm
          open={isDialogOpen}
          onOpenChange={handleDialogClose}
          editingKPI={editingKPI}
          users={users}
          onSubmit={handleSubmitKPI}
          saving={saving}
          isAdmin={canEditKPIs}
        />
      </>
    );
  }

  // Main KPIs list view
  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (<Skeleton key={i} className="h-48" />))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden" data-testid="kpis-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">KPIs</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Gestiona y analiza tus indicadores</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1" role="group" aria-label="Modo de visualización">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 px-2 sm:px-3 focus-visible:ring-2 focus-visible:ring-slate-400',
                  viewMode === 'card' 
                    ? 'bg-white shadow-sm text-slate-900' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                )}
                onClick={() => setViewMode('card')}
                data-testid="view-card-btn"
                aria-label="Vista en tarjetas"
                aria-pressed={viewMode === 'card'}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 px-2 sm:px-3 focus-visible:ring-2 focus-visible:ring-slate-400',
                  viewMode === 'list' 
                    ? 'bg-white shadow-sm text-slate-900' 
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                )}
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
                aria-label="Vista en lista"
                aria-pressed={viewMode === 'list'}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Admin-only buttons */}
            {canEditKPIs && (
              <div className="flex items-center gap-2 ml-auto">
                {isAdmin() && (
                  <Button
                    type="button"
                    variant={isManagementMode ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={toggleManagementMode}
                    data-testid="manage-kpis-btn"
                    className={cn(isManagementMode && 'bg-slate-200')}
                  >
                    <Settings2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{isManagementMode ? 'Salir' : 'Gestionar'}</span>
                  </Button>
                )}
                {!isManagementMode && (
                  <>
                    {isAdmin() && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/kpis/trash')}
                        className="text-red-500 hover:text-red-700"
                        data-testid="kpi-trash-btn"
                      >
                        <Trash2 className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Papelera</span>
                      </Button>
                    )}
                    <Button 
                      type="button"
                      onClick={handleCreateKPI}
                      size="sm" 
                      data-testid="create-kpi-btn"
                    >
                      <Plus className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Nuevo KPI</span>
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Filters - Only Area filter visible */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar KPIs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-kpis"
            />
          </div>
          
          {/* Area Filter - Single filter for the platform */}
          <Select 
            value={filterArea} 
            onValueChange={(value) => setFilterArea(value)}
          >
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-area">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Área" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las áreas</SelectItem>
              {areas.map((area) => (
                <SelectItem key={area} value={area}>{area}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* NOTE: Scope filter removed from UI. 
              Backend scope is kept at 'all' (default) for compatibility.
              Security/permissions by teams still work via backend. */}
          
          {/* Clear Filters Button - Only shows when area or search is active */}
          {(filterArea !== 'all' || searchTerm) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterArea('all');
                setSearchTerm('');
              }}
              className="text-slate-500 hover:text-slate-700"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>

        {/* Bulk Action Bar - Admin Only in Management Mode */}
        {isAdmin() && isManagementMode && (
          <div className="bg-slate-900 text-white rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 max-w-full overflow-hidden" data-testid="bulk-action-bar">
            <div className="flex items-center gap-3 min-w-0">
              <Checkbox
                id="select-all"
                checked={selectedKPIs.size === filteredKPIs.length && filteredKPIs.length > 0}
                onCheckedChange={selectAllKPIs}
                className="border-white data-[state=checked]:bg-white data-[state=checked]:text-slate-900 flex-shrink-0"
              />
              <label htmlFor="select-all" className="text-sm cursor-pointer truncate">
                {selectedKPIs.size === 0 
                  ? 'Seleccionar todos' 
                  : `${selectedKPIs.size} de ${filteredKPIs.length} seleccionados`}
              </label>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {selectedKPIs.size > 0 ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => handleBulkAction('activate')}
                    disabled={bulkActionLoading}
                    className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-activate-btn"
                  >
                    <Power className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Activar</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleBulkAction('hide_dashboard')}
                    disabled={bulkActionLoading}
                    className="flex-1 sm:flex-none"
                    data-testid="bulk-hide-btn"
                  >
                    <EyeOff className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Omitir</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleBulkAction('deactivate')}
                    disabled={bulkActionLoading}
                    className="flex-1 sm:flex-none"
                    data-testid="bulk-deactivate-btn"
                  >
                    <Power className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Desactivar</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={bulkActionLoading}
                    className="flex-1 sm:flex-none"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </Button>
                </>
              ) : (
                <span className="text-sm text-slate-400">Selecciona KPIs para ver acciones</span>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleManagementMode}
                className="text-white hover:bg-white/10"
                data-testid="exit-management-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Edit Order Toggle & Controls */}
        {!isManagementMode && displayKpis.length > 1 && canEditKPIs && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant={isEditOrderMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsEditOrderMode(!isEditOrderMode)}
                className={cn(
                  isEditOrderMode && "bg-blue-600 hover:bg-blue-700"
                )}
                data-testid="toggle-edit-order-btn"
              >
                <GripVertical className="w-4 h-4 mr-1.5" />
                {isEditOrderMode ? 'Finalizar edición' : 'Editar orden'}
              </Button>
              
              {isEditOrderMode && (
                <>
                  <span className="text-sm text-slate-500 hidden sm:inline">
                    Arrastra los elementos para reordenar
                  </span>
                  {orderSaving && (
                    <span className="text-sm text-blue-500 animate-pulse">
                      Guardando...
                    </span>
                  )}
                </>
              )}
            </div>
            
            {isEditOrderMode && isManualOrder && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  resetOrder();
                  toast.success('Orden restablecido');
                }}
                className="text-slate-500 hover:text-slate-700"
                data-testid="reset-order-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Restablecer orden</span>
              </Button>
            )}
          </div>
        )}

        {/* KPIs Visualization */}
        {displayKpis.length === 0 ? (
          <KPIEmptyState message="No hay KPIs disponibles" />
        ) : viewMode === 'card' ? (
          isEditOrderMode ? (
            <SortableContainer
              items={displayKpis}
              onDragEnd={handleDragEnd}
              strategy="grid"
              disabled={false}
            >
              <div className="kpi-grid">
                {displayKpis.map((kpi, index) => (
                  <SortableItem key={kpi.id} id={kpi.id} disabled={false}>
                    {({ attributes, listeners, isDragging }) => (
                      <KPICard
                        kpi={kpi}
                        isAdmin={canEditKPIs}
                        isManagementMode={false}
                        isSelected={false}
                        onSelect={() => {}}
                        onClick={() => {}} // Block clicks in edit mode
                        onEdit={() => {}}
                        dragHandleProps={{ ...attributes, ...listeners }}
                        isDragging={isDragging}
                        showDragHandle={true}
                        onMoveUp={() => moveKpi(kpi.id, 'up')}
                        onMoveDown={() => moveKpi(kpi.id, 'down')}
                        canMoveUp={index > 0}
                        canMoveDown={index < displayKpis.length - 1}
                      />
                    )}
                  </SortableItem>
                ))}
              </div>
            </SortableContainer>
          ) : (
            <div className="kpi-grid">
              {displayKpis.map((kpi) => (
                <KPICard
                  key={kpi.id}
                  kpi={kpi}
                  isAdmin={canEditKPIs}
                  isManagementMode={isManagementMode}
                  isSelected={selectedKPIs.has(kpi.id)}
                  onSelect={toggleKPISelection}
                  onClick={() => handleKPIClick(kpi)}
                  onEdit={(kpi, e) => handleEditKPI(kpi, e)}
                  onDelete={(kpi) => setDeleteTarget(kpi)}
                  showDragHandle={false}
                />
              ))}
            </div>
          )
        ) : (
          <KPIListView
            kpis={displayKpis}
            isAdmin={canEditKPIs}
            isManagementMode={isManagementMode}
            selectedKPIs={selectedKPIs}
            onSelectAll={selectAllKPIs}
            onSelectKPI={toggleKPISelection}
            onKPIClick={(kpi) => handleKPIClick(kpi)}
            onEditKPI={(kpi, e) => handleEditKPI(kpi, e)}
            // Drag & drop props - only enabled in edit order mode
            isEditOrderMode={isEditOrderMode}
            onDragEnd={isEditOrderMode ? handleDragEnd : undefined}
            onMoveKpi={isEditOrderMode ? moveKpi : undefined}
          />
        )}

        {/* Create/Edit KPI Dialog */}
        <KPIForm
          open={isDialogOpen}
          onOpenChange={handleDialogClose}
          editingKPI={editingKPI}
          users={users}
          onSubmit={handleSubmitKPI}
          saving={saving}
          isAdmin={canEditKPIs}
        />

        {/* Delete Confirmation Dialog - Admin Only */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
                <AlertCircle className="w-5 h-5" />
                Confirmar eliminación
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  ¿Estás seguro de que deseas eliminar <strong>{selectedKPIs.size} KPI{selectedKPIs.size !== 1 ? 's' : ''}</strong>?
                </p>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-rose-800 mb-2">KPIs a eliminar:</p>
                  <ul className="list-disc list-inside text-rose-700 max-h-32 overflow-y-auto">
                    {getSelectedKPINames().slice(0, 5).map((name, i) => (
                      <li key={i} className="truncate">{name}</li>
                    ))}
                    {getSelectedKPINames().length > 5 && (
                      <li className="text-rose-500">...y {getSelectedKPINames().length - 5} más</li>
                    )}
                  </ul>
                </div>
                <p className="text-sm text-slate-500">
                  Esta acción eliminará los KPIs pero <strong>no</strong> sus datos históricos de resultados y metas.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleBulkAction('delete')}
                className="bg-rose-600 hover:bg-rose-700"
                disabled={bulkActionLoading}
                data-testid="confirm-delete-btn"
              >
                {bulkActionLoading ? 'Eliminando...' : 'Sí, eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Soft Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">Eliminar KPI</DialogTitle>
              <DialogDescription>
                Se eliminará <strong>{deleteTarget?.name}</strong> de la operación activa. No se borrará su historial de metas ni resultados. Solo un administrador podrá restaurarlo desde la papelera.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={handleSoftDelete}
                disabled={deleting}
                data-testid="confirm-soft-delete-btn"
              >
                {deleting ? 'Eliminando...' : 'Eliminar KPI'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default KPIs;
